import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UitkNewDatePicker, UitkNewDatePickerProps } from 'uitk-react-new-date-picker';

export interface NewDateFieldProps {
  analytics?: AnalyticsStore;
  context?: Context;
  dateFieldProps: object;
  fetchDatePickerRates?: () => Promise<object>;
  isDateless?: boolean;
  midnightBookingOffsetHours?: number;
  numDaysBookableInAdvance?: number;
  onChange: any;
  registerFocusMethod?: (fieldName: string, focusMethod: () => void) => void;
  rfrr: object;
  routeStore?: RouteStore;
}

export type LocalizedNewDateFieldProps = LocalizedComponentProps & UitkNewDatePickerProps & NewDateFieldProps;

export interface NewDateFieldState {
  mounted: boolean;
  showDatePicker: boolean;
}

@withLocalization
@inject('routeStore', 'analytics', 'context')
export class NewDateField extends React.Component<LocalizedNewDateFieldProps, NewDateFieldState> {
  constructor(props) {
    super(props);

    this.focusButton = React.createRef();

    this.state = {
      mounted: false,
      showDatePicker: false,
    };

    this.focusDateFieldInput = this.focusDateFieldInput.bind(this);
    this.onMount = this.onMount.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.save = this.save.bind(this);
  }

  public focusButton = null;
  public startRef;
  public endRef;
  private datePickerAnalytics = {};

  public static defaultProps = {
    endDate: '',
    startDate: '',
    l10n: null,
  };

  public componentDidMount() {
    const { registerFocusMethod } = this.props;
    registerFocusMethod('endDate', this.focusDateFieldInput);

    this.setState({ mounted: true });
  }

  public open(dateType, isMobile) {
    const { analytics, rfrr, routeStore } = this.props;

    if (isMobile) {
      routeStore.updateSearch({ pwaDialog: `datepicker-${dateType}` });
    }

    this.setState({ showDatePicker: true });
  }

  public save(start, end, isMobile) {
    const { endDate, l10n, onChange, startDate } = this.props;
    const { formatDate } = l10n;
    const formattedEnd = formatDate(end, { raw: 'yyyy-MM-dd' });
    const formattedStart = formatDate(start, { raw: 'yyyy-MM-dd' });

    // prevents submitting the datepicker when no new dates are selected
    if (endDate !== formattedEnd || startDate !== formattedStart) {
      onChange({
        target: {
          name: 'startDate',
          value: formattedStart,
        },
      });

      onChange({
        target: {
          name: 'endDate',
          value: formattedEnd,
        },
      });
    }

    this.dismiss(isMobile, false);
  }

  public focusDateFieldInput(): void {
    const { dateFieldProps } = this.props;
    const { endInvalid, startInvalid } = dateFieldProps;

    if (startInvalid) {
      this.focusButton = this.startRef && this.startRef.current;
    }

    if (endInvalid) {
      this.focusButton = this.endRef && this.endRef.current;
    }

    // manage focus back to the trigger
    if (this.focusButton && typeof this.focusButton.focus === 'function') {
      this.focusButton.focus();
    }
  }

  public dismiss(isMobile, noUpdate) {
    if (isMobile) {
      this.props.routeStore.goBack();
    }

    if (!noUpdate) {
      this.setState({ showDatePicker: false });
    }
  }

  public onMount(start, end) {
    this.startRef = start;
    this.endRef = end;
  }

  public render() {
    const {
      buttonText,
      context,
      dateFieldProps,
      endDate,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
      startDate,
    } = this.props;

    const { mounted, showDatePicker } = this.state;

    dateFieldProps['showDatePicker'] = showDatePicker;

    if (!mounted) {
      dateFieldProps['endPlaceholder'] = context.localDateFormat;
      dateFieldProps['startPlaceholder'] = context.localDateFormat;
    }

    const dialogWrapper = (children, dateType) => (
      <QueryRoute query={{ pwaDialog: `datepicker-${dateType}` }}>
        <Layer id={`home-datepicker-${dateType}`} onDismiss={this.dismiss}>
          {children}
        </Layer>
      </QueryRoute>
    );

    const datePickerProps = {
      analytics: this.datePickerAnalytics,
      buttonText,
      dateFieldProps,
      daysBookableInAdvance: numDaysBookableInAdvance,
      dialogWrapper,
      doubleMonths: false,
      endDate,
      midnightBookingOffset: midnightBookingOffsetHours,
      onDismissCB: this.dismiss,
      onMountCB: this.onMount,
      onOpenCB: this.open,
      onSubmitCB: this.save,
      startDate,
    };

    return <UitkNewDatePicker {...datePickerProps} />;
  }
}
